import React, { useEffect, useState, useRef } from "react";
import { useKeycloakToken } from '../common/useKeycloak';
import { useNavigate } from 'react-router-dom';
import { ProviderType, buildProviderType, HIDE_TECHNOLOGY_CARD_SENTINEL } from "../common/ObjectTypes";
import { loadProviderTypes } from '../../api/connections/loadProviderTypesNewConnection';
import { useTranslation } from "react-i18next";
import NewConnectionStepOne from "./NewConnectionStepOne";
import { useKeycloak } from "@react-keycloak/web";
import GundiV1Modal from "./GundiV1Modal";
import ConnectionProgressIndicator from "./NewConnectionProgressIndicator/ConnectionProgressIndicator";
import { trackConnectionCreationStarted, trackConnectionCreationAbandoned } from '../../analytics/analytics';
import { containerClasses, buttonClasses, textClasses } from "../common/tailwindClassnames";

export function ManageAddConnectionStepOne() {
    type FilterTypes = {
        endpoint__in: FilterTags[],
        enabled: FilterTags[],
        type__in: FilterTags[],
        owner__in: FilterTags[],
        ordering: string,
        search: string
    };
    function createFilterTypes(
        endpoint__in: FilterTags[],
        enabled: FilterTags[],
        type__in: FilterTags[],
        owner__in: FilterTags[],
        ordering: string,
        search: string
    ): FilterTypes {
        return {
            endpoint__in: endpoint__in || [],
            enabled: enabled || [],
            type__in: type__in || [],
            owner__in: owner__in || [],
            ordering: ordering || "",
            search: search || ""
        }
    }
    type FilterTags = {
        name: string,
        id: string
    };
    const { t } = useTranslation();
    const [token, showLoginModal, setShowLoginModal] = useKeycloakToken();
    const { keycloak, initialized } = useKeycloak();

    const [providerTypes, setProviderTypes] = useState<ProviderType[]>([]);
    const [reloadProviderTypes, setReloadProviderTypes] = useState(true);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState(false);
    const [v1Modal, setV1Modal] = useState<boolean>(false);
    const [searchStr, setSearchStr] = useState("");
    const [debouncedSearchStr, setDebouncedSearchStr] = useState("");
    const searchTimeout = useRef<NodeJS.Timeout | null>(null);
    const isInitialRender = useRef(true);
    const abortControllerRef = useRef<AbortController | null>(null);

    const [selectedProviderType, setSelectedProviderType] = useState<ProviderType>(buildProviderType());
    const [isProviderTypeSelected, setIsProviderTypeSelected] = useState(false);
    const [pageLoaded, setPageLoaded] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const navigate = useNavigate();

    const loadProviderTypesData = async (search_str: string) => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            // Cancel any in-flight request before starting a new one
            abortControllerRef.current?.abort();
            abortControllerRef.current = new AbortController();
            const signal = abortControllerRef.current.signal;

            setLoading(true);

            const [provider_types, error] = await loadProviderTypes(token, search_str, signal);

            if (signal.aborted) return;

            if (error) {
                setFetchError(true);
            } else {
                setFetchError(false);
                setProviderTypes(provider_types.filter(pt => pt.description !== HIDE_TECHNOLOGY_CARD_SENTINEL));
            }

            setLoading(false);
            setReloadProviderTypes(false);
            setPageLoaded(true);
        }
    };

    useEffect(() => {
        trackConnectionCreationStarted();
    }, []);

    useEffect(() => {
        if (reloadProviderTypes) loadProviderTypesData(searchStr);
    }, [token, reloadProviderTypes]);

    useEffect(() => {
        if (isInitialRender.current) {
            isInitialRender.current = false;
            return;
        }
        loadProviderTypesData(debouncedSearchStr);
    }, [debouncedSearchStr]);

    useEffect(() => {
        return () => {
            if (searchTimeout.current) clearTimeout(searchTimeout.current);
            abortControllerRef.current?.abort();
        };
    }, []);

    const handleSelectProviderType = (pt: ProviderType) => {
        if (pt.id === "") {
            setSelectedProviderType(pt);
            setV1Modal(true);
        } else {
            setIsProviderTypeSelected(true);
            setSelectedProviderType(pt);
        }
    };

    const handleCloseModal = () => {
        setV1Modal(false);
    };

    const handleChangeProviderTypeSearchBar = (e: any) => {
        const value = e.target.value;
        setSearchStr(value);

        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }

        if (value.length >= 3 || value.length === 0) {
            searchTimeout.current = setTimeout(() => {
                setDebouncedSearchStr(value);
            }, 500);
        }
    };

    const handleClickNext = () => {
        navigate(`/connections/new/configureprovider/${selectedProviderType.value}`)
    };

    const handleCancel = () => {
        trackConnectionCreationAbandoned(1);
        navigate("/")
    };

    return (
            <div className={containerClasses.page}>
                {v1Modal &&
                    <GundiV1Modal
                        helpCenterUrl={selectedProviderType.help_center_url || ""}
                        onToggleModal={handleCloseModal}
                    />
                }
                <div className={containerClasses.header}>
                    <div className="flex flex-col">
                        <div>
                            <h1 className={textClasses.heading}>{t("New Connection")}</h1>
                        </div>
                    </div>
                </div>
                <div className="px-12 pt-5 -mt-5 -mx-5 xl:-mx-12 transition-colors duration-300">
                    <ConnectionProgressIndicator
                        stepZeroStatus="in-progress"
                        stepOneStatus="upcoming"
                        stepTwoStatus="upcoming"
                        stepThreeStatus="upcoming"
                    />
                    <NewConnectionStepOne
                        providerTypes={providerTypes}
                        reloadProviderTypes={reloadProviderTypes}
                        loading={loading}
                        fetchError={fetchError}
                        onSelectProviderType={handleSelectProviderType}
                        onSearchProviderType={handleChangeProviderTypeSearchBar}
                    />
                </div>

                <div className="fixed flex flex-row bottom-0 right-0 pr-5 py-3 pl-16 bg-white/10 backdrop-blur w-full shadow-lg transition-colors duration-300">
                    <button
                        className={`px-8 py-2 font-bold rounded mr-3 transition-colors duration-300 ${!isProviderTypeSelected
                                ? "bg-gray-lines text-white"
                                : buttonClasses.primary
                            }`}
                        onClick={() => handleClickNext()}
                        disabled={!isProviderTypeSelected}
                    >
                        {currentStep === 3 ? t("Finish") : t("Next")}
                    </button>
                    <button
                        className={buttonClasses.secondary}
                        type="button"
                        onClick={handleCancel}
                    >
                        {t("Cancel")}
                    </button>
                </div>
            </div>
        )
}